import React, { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { api } from "@/lib/api";
import { Btn, Input, Card, Modal, Badge } from "@/components/UI";
import { ShieldCheck, KeyRound, AlertTriangle, CheckCircle2, Copy } from "lucide-react";

// iter224 — MFA (TOTP) admin security tab.  Turkish UI.
export default function SecurityMFA() {
  const [status, setStatus] = useState(null);
  const [password, setPassword] = useState("");
  const [enrollment, setEnrollment] = useState(null); // { provisioning_uri, issuer, account }
  const [code, setCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [showDisable, setShowDisable] = useState(false);
  const [showRegenerate, setShowRegenerate] = useState(false);

  const reload = async () => {
    try { setStatus(await api.get("/mfa/status")); }
    catch { setStatus(null); }
  };
  useEffect(() => { reload(); }, []);

  const start = async () => {
    setBusy(true); setErr("");
    try {
      const r = await api.post("/mfa/enroll/start", { password });
      setEnrollment(r);
      setPassword("");
    } catch (e) { setErr(e.response?.data?.detail || "Kurulum başlatılamadı"); }
    finally { setBusy(false); }
  };

  const confirm = async () => {
    setBusy(true); setErr("");
    try {
      const r = await api.post("/mfa/enroll/confirm", { code });
      setRecoveryCodes(r.recovery_codes);
      setEnrollment(null);
      setCode("");
      await reload();
    } catch (e) { setErr(e.response?.data?.detail || "Doğrulama başarısız"); }
    finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true); setErr("");
    try {
      await api.post("/mfa/disable", { password, code });
      setPassword(""); setCode(""); setShowDisable(false);
      await reload();
    } catch (e) { setErr(e.response?.data?.detail || "MFA kapatılamadı"); }
    finally { setBusy(false); }
  };

  const regenerate = async () => {
    setBusy(true); setErr("");
    try {
      const r = await api.post("/mfa/recovery/regenerate", { password, code });
      setRecoveryCodes(r.recovery_codes);
      setPassword(""); setCode(""); setShowRegenerate(false);
      await reload();
    } catch (e) { setErr(e.response?.data?.detail || "Kurtarma kodları yenilenemedi"); }
    finally { setBusy(false); }
  };

  const copyUri = () => {
    if (enrollment?.provisioning_uri) {
      navigator.clipboard.writeText(enrollment.provisioning_uri);
    }
  };

  if (!status) return <Card className="p-4">Yükleniyor…</Card>;
  if (!status.mfa_scope_available) {
    return (
      <Card className="p-4">
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <AlertTriangle size={16} className="text-amber-500"/>
          MFA yalnızca Sistem Yöneticisi hesapları için kullanılabilir.
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card className="p-4" data-testid="mfa-status-card">
        <div className="flex items-center gap-3 mb-2">
          <ShieldCheck size={22} className={status.mfa_enabled ? "text-emerald-600" : "text-slate-400"}/>
          <div className="flex-1">
            <div className="font-semibold text-sm">Çok Faktörlü Kimlik Doğrulama (MFA)</div>
            <div className="text-xs text-slate-500">
              {status.mfa_enabled ? "Aktif" : "Kapalı"} — TOTP tabanlı (Google Authenticator, Authy, 1Password, Microsoft Authenticator ile uyumlu)
            </div>
          </div>
          <Badge tone={status.mfa_enabled ? "success" : "muted"} data-testid="mfa-status-badge">
            {status.mfa_enabled ? "AKTİF" : "KAPALI"}
          </Badge>
        </div>
        {status.mfa_enabled && (
          <div className="text-xs text-slate-500 mt-2">
            Kalan tek kullanımlık kurtarma kodu: <b>{status.recovery_remaining}</b>
          </div>
        )}
      </Card>

      {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{err}</div>}

      {/* Enrollment: password step */}
      {!status.mfa_enabled && !enrollment && !recoveryCodes && (
        <Card className="p-4 space-y-3">
          <div className="text-sm font-semibold">MFA'yı Etkinleştir</div>
          <div className="text-xs text-slate-600">
            Etkinleştirmek için önce hesap şifrenizi doğrulayın. Kurulum sırasında telefonunuzda bir doğrulayıcı uygulama olmalıdır.
          </div>
          <Input type="password" label="Mevcut şifreniz" value={password} onChange={e => setPassword(e.target.value)} data-testid="mfa-password"/>
          <Btn onClick={start} disabled={busy || !password} data-testid="mfa-start-btn">
            {busy ? "İşleniyor…" : "MFA Kurulumunu Başlat"}
          </Btn>
        </Card>
      )}

      {/* Enrollment: QR + code step */}
      {enrollment && (
        <Card className="p-4 space-y-3" data-testid="mfa-enroll-card">
          <div className="text-sm font-semibold">Adım 2: Uygulamayla Eşleştir</div>
          <div className="text-xs text-slate-600">
            Doğrulayıcı uygulamanızı açın ve aşağıdaki QR kodunu tarayın. Kod taranamıyorsa, otpauth linkini kopyalayıp elle ekleyebilirsiniz.
          </div>
          <div className="flex flex-col sm:flex-row gap-4 items-start">
            <div className="p-3 bg-white border border-slate-200 rounded" data-testid="mfa-qr">
              <QRCodeSVG value={enrollment.provisioning_uri} size={192}/>
            </div>
            <div className="flex-1 space-y-2 text-xs">
              <div><b>Hesap:</b> {enrollment.account}</div>
              <div><b>Uygulama:</b> {enrollment.issuer}</div>
              <button type="button" onClick={copyUri} className="flex items-center gap-1 px-2 py-1 border border-slate-300 rounded hover:bg-slate-50" data-testid="mfa-copy-uri">
                <Copy size={12}/> otpauth bağlantısını kopyala
              </button>
            </div>
          </div>
          <Input label="Uygulamadaki 6 haneli kod" value={code} onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} data-testid="mfa-confirm-code" placeholder="000000"/>
          <div className="flex gap-2">
            <Btn onClick={confirm} disabled={busy || code.length !== 6} data-testid="mfa-confirm-btn">
              {busy ? "Doğrulanıyor…" : "Doğrula ve Etkinleştir"}
            </Btn>
            <Btn variant="secondary" onClick={() => { setEnrollment(null); setCode(""); }}>Vazgeç</Btn>
          </div>
        </Card>
      )}

      {/* Recovery-code display */}
      {recoveryCodes && (
        <Card className="p-4 space-y-3 border-amber-300 bg-amber-50" data-testid="mfa-recovery-card">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <KeyRound size={16} className="text-amber-700"/>
            Kurtarma Kodlarınız — Bunları hemen güvenli bir yere kaydedin
          </div>
          <div className="text-xs text-amber-800">
            Bu kodlar yalnızca <b>bir kez</b> gösterilir ve her biri yalnızca <b>bir defa</b> kullanılabilir. Doğrulayıcı uygulamanıza erişemezseniz bu kodları kullanarak giriş yapabilirsiniz.
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            {recoveryCodes.map((c, i) => (
              <div key={i} className="font-mono text-sm bg-white border border-amber-300 rounded px-2 py-1 text-center">{c}</div>
            ))}
          </div>
          <div className="flex gap-2">
            <Btn onClick={() => {
              const text = recoveryCodes.join("\n");
              navigator.clipboard.writeText(text);
              alert("Kurtarma kodları panoya kopyalandı.");
            }} data-testid="mfa-copy-recovery">
              Tümünü Panoya Kopyala
            </Btn>
            <Btn variant="secondary" onClick={() => setRecoveryCodes(null)} data-testid="mfa-recovery-dismiss">Kaydettim, Kapat</Btn>
          </div>
        </Card>
      )}

      {/* Manage: disable + regenerate */}
      {status.mfa_enabled && !recoveryCodes && (
        <Card className="p-4 space-y-3">
          <div className="text-sm font-semibold">MFA Yönetimi</div>
          <div className="flex flex-wrap gap-2">
            <Btn variant="secondary" onClick={() => setShowRegenerate(true)} data-testid="mfa-regenerate-btn">
              Kurtarma Kodlarını Yenile
            </Btn>
            <Btn variant="secondary" onClick={() => setShowDisable(true)} className="border-red-300 text-red-700 hover:bg-red-50" data-testid="mfa-disable-btn">
              MFA'yı Kapat
            </Btn>
          </div>
        </Card>
      )}

      <Modal open={showDisable} onClose={() => setShowDisable(false)} title="MFA'yı Kapat">
        <div className="space-y-3">
          <div className="text-xs text-slate-600">
            MFA'yı devre dışı bırakmak için şifrenizi ve mevcut bir 6 haneli TOTP kodunu veya bir kurtarma kodunu girin.
          </div>
          <Input type="password" label="Mevcut şifreniz" value={password} onChange={e => setPassword(e.target.value)} data-testid="mfa-disable-password"/>
          <Input label="TOTP kodu veya Kurtarma kodu" value={code} onChange={e => setCode(e.target.value.toUpperCase())} data-testid="mfa-disable-code"/>
          <div className="flex gap-2">
            <Btn onClick={disable} disabled={busy || !password || !code} className="bg-red-600 hover:bg-red-700" data-testid="mfa-disable-submit">
              {busy ? "Kapatılıyor…" : "MFA'yı Kapat"}
            </Btn>
            <Btn variant="secondary" onClick={() => setShowDisable(false)}>Vazgeç</Btn>
          </div>
        </div>
      </Modal>

      <Modal open={showRegenerate} onClose={() => setShowRegenerate(false)} title="Kurtarma Kodlarını Yenile">
        <div className="space-y-3">
          <div className="text-xs text-slate-600">
            Yeni kurtarma kodları üretmek için şifrenizi ve mevcut 6 haneli TOTP kodunu girin. Yenileme sonrası eski kodlar geçersiz olur.
          </div>
          <Input type="password" label="Mevcut şifreniz" value={password} onChange={e => setPassword(e.target.value)} data-testid="mfa-regen-password"/>
          <Input label="TOTP kodu" value={code} onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="000000" data-testid="mfa-regen-code"/>
          <div className="flex gap-2">
            <Btn onClick={regenerate} disabled={busy || !password || code.length !== 6} data-testid="mfa-regen-submit">
              {busy ? "Yenileniyor…" : "Kurtarma Kodlarını Yenile"}
            </Btn>
            <Btn variant="secondary" onClick={() => setShowRegenerate(false)}>Vazgeç</Btn>
          </div>
        </div>
      </Modal>
    </div>
  );
}
